import { servicesData } from './servicesData';

export type LocalizedText = {
  zh: string;
  en: string;
};

export type ServiceContent = {
  id: string;
  cardTitle: string;
  cardDescription: string;
  pageTitle: string;
  pageDescription: string;
  pageContent: string;
  featureList: string;
  image: string;
  ctaText: string;
};

export type TeamProfileContent = {
  id: string;
  name: LocalizedText;
  image: string;
  imageAlt: LocalizedText;
  imageCaption: LocalizedText;
  summary: LocalizedText;
  visible: boolean;
};

export type LandingContent = {
  heroSubtitle: LocalizedText;
  aboutTitle: LocalizedText;
  aboutIntro: LocalizedText;
  crossBorderTitle: LocalizedText;
  crossBorderParagraphs: LocalizedText[];
  aboutImage: {
    src: string;
    alt: LocalizedText;
    caption: LocalizedText;
  };
  teamTitle: LocalizedText;
  teamIntro: LocalizedText;
};

export type OfficeContent = {
  title: LocalizedText;
  addressLines: string[];
  phone?: string;
  fax?: string;
  email?: string;
};

export type ContactContent = {
  firmName: LocalizedText;
  pageTitle: LocalizedText;
  intro: LocalizedText;
  torontoOffice: OfficeContent;
  beijingOffice: OfficeContent;
  footerTagline: LocalizedText;
};

export type SiteContentDoc = {
  landing: LandingContent;
  services: ServiceContent[];
  team: TeamProfileContent[];
  contact: ContactContent;
};

const emptyLocalizedText = (): LocalizedText => ({ zh: '', en: '' });

const defaultServiceCardDescriptions: Record<string, string> = {
  'civil-litigation': 'Led by Selina Zeng, providing strategic and results-oriented advocacy for complex disputes.',
  'business-law': 'Comprehensive legal support for business growth, formation, and successful transactions.',
  'marriage-family-law': 'Strategic negotiation and efficient settlement for complex and cross-border family matters.',
  'real-estate-law': 'Full spectrum of real estate advice with precision in execution and control over risk.',
  'estates-wealth-succession': 'Preserving assets and ensuring efficient transfer of wealth across generations.',
};

const defaultServiceOrder = [
  'civil-litigation',
  'business-law',
  'marriage-family-law',
  'real-estate-law',
  'estates-wealth-succession',
] as const;

const defaultServicesContent = (): ServiceContent[] => {
  return defaultServiceOrder
    .map((serviceId) => servicesData[serviceId])
    .filter((service): service is NonNullable<typeof service> => !!service)
    .map((service) => ({
      id: service.id,
      cardTitle: service.id === 'business-law' ? 'Business Law' : service.title,
      cardDescription: defaultServiceCardDescriptions[service.id] || service.description,
      pageTitle: service.title,
      pageDescription: service.description,
      pageContent: service.fullContent,
      featureList: service.features.join('\n'),
      image: service.image,
      ctaText: service.ctaText,
    }));
};

export const createDefaultSiteContent = (): SiteContentDoc => ({
  landing: {
    heroSubtitle: {
      zh: '',
      en: 'Understand what the Chinese people are thinking. A Beacon of Hope for Yaohua\'s People.',
    },
    aboutTitle: {
      zh: '',
      en: 'About Us',
    },
    aboutIntro: {
      zh: '',
      en: 'At Y. Liu Law, we serve every client with honesty, care, and respect. We are committed to delivering responsive, thoughtful legal support that consistently goes above and beyond the expected standard of service.',
    },
    crossBorderTitle: {
      zh: '',
      en: 'Your Cross-Border Partners',
    },
    crossBorderParagraphs: [
      {
        zh: '',
        en: 'We are an award-winning law firm based in Toronto (Unionville) and Beijing (Huacheng Law Firm). Our team specializes in bilingual cross-border legal services, focusing on the migration experience and legal needs of the cross-border Chinese community.',
      },
      {
        zh: '',
        en: 'Whether you are looking to establish a business in Canada or need representation in civil litigation, our global presence ensures you have the support you need, where you need it.',
      },
    ],
    aboutImage: {
      src: '/images/toronto-skyline-517x345.jpg',
      alt: {
        zh: '',
        en: 'Toronto skyline',
      },
      caption: emptyLocalizedText(),
    },
    teamTitle: {
      zh: '',
      en: 'Our Team',
    },
    teamIntro: {
      zh: '',
      en: 'Meet the lawyers behind Y. Liu Law Firm\'s cross-border practice. Each profile reflects a distinct area of strength, with shared commitment to clear advice, strong execution, and long-term client trust.',
    },
  },
  services: defaultServicesContent(),
  team: [
    {
      id: 'selina-zeng',
      name: {
        zh: '',
        en: 'Selina Zeng',
      },
      image: '/images/team/selina-zeng.jpg',
      imageAlt: {
        zh: '',
        en: 'Selina Zeng portrait',
      },
      imageCaption: emptyLocalizedText(),
      summary: {
        zh: '',
        en: 'Lawyer Selina Zeng has been providing legal counsel to high-net-worth individuals, investors, and business leaders in the Greater Toronto Area for an extended period, helping clients strike a balance between wealth growth, corporate control, and asset protection.\n\nHer practice encompasses real estate, corporate structuring, business affairs, and wealth and estate planning, with a particular emphasis on risk management, asset segregation, and long-term wealth protection strategies.\n\nSelina is highly regarded by clients for her ability to quickly untangle complex issues, accurately identify core risks, and propose clear, actionable solutions. She frequently engages in complex business transactions, multi-party negotiations, and cross-border matters, balancing legal analysis with an emphasis on business realities and long-term stability.\n\nWith her proficiency in both English and Mandarin, Selina is able to provide efficient and seamless communication and service to clients from diverse backgrounds. Her professional style, characterized by prudence, precision, and strategic judgment, has earned her the long-term trust of her clients.',
      },
      visible: true,
    },
    {
      id: 'maryam-abdulhaq',
      name: {
        zh: '',
        en: 'Maryam Abdulhaq',
      },
      image: '/images/team/maryam-abdulhaq.png',
      imageAlt: {
        zh: '',
        en: 'Maryam Abdulhaq portrait',
      },
      imageCaption: emptyLocalizedText(),
      summary: {
        zh: '',
        en: 'Maryam Abdulhaq is a practicing lawyer in Ontario, Canada, known for her calm, composed, and strategically astute approach to handling significant matters that are both legally complex and personally impactful.\n\nHer practice encompasses immigration law, family law, and real estate law, including refugee applications, immigration appeals, admissibility hearings, humanitarian and compassionate grounds applications, as well as divorce, child custody and decision-making responsibilities, spousal and child support, and residential and commercial property transactions.\n\nMaryam firmly adheres to a customer-centric approach and places great importance on communication and understanding within diverse cultural contexts. She combines meticulous legal analysis, pragmatic judgment, and effective advocacy to help clients navigate complex situations with high stakes, time sensitivity, and emotional stress, ensuring that matters can still be progressed clearly and methodically.\n\nWith her proficiency in multiple languages and extensive experience in cross-cultural services, Maryam is able to provide professional, meticulous, and empathetic legal support to clients from diverse backgrounds, helping them gain a clearer understanding of complex legal procedures and instill greater confidence in their endeavors.',
      },
      visible: true,
    },
    {
      id: 'yan-liu',
      name: {
        zh: '',
        en: 'Yan Liu',
      },
      image: '/images/team/yan-liu1.jpg',
      imageAlt: {
        zh: '',
        en: 'Yan Liu portrait',
      },
      imageCaption: emptyLocalizedText(),
      summary: {
        zh: '',
        en: 'Lawyer Liu Yan holds legal practice qualifications in both China and the province of Ontario, Canada. She has specialized in handling complex legal matters involving personal, family, and business interests for an extended period.\n\nHer practice areas encompass real estate transactions, family law, estate and wealth transfer, corporate and business transactions, as well as the design of family wealth structures for high-net-worth individuals, including trusts, asset protection, and intergenerational succession arrangements.\n\nDrawing on dual legal backgrounds in China and Canada, as well as a cross-border perspective, lawyer Liu Yan continues to handle local and international matters for business leaders, high-net-worth individuals, and family clients involving both China and Canada. She provides strategic solutions that balance legal depth with practicality, navigating complex relationships, business realities, and long-term risks.\n\nThe reason clients trust her lies not only in her professional judgment and execution capabilities, but also in her ability to maintain a clear direction and stable leadership even in times of change and pressure.\n\nWhat she provides is more than just legal advice.\n\nIt also involves a holistic consideration of family, assets, businesses, and future development.\n\nYan Law Firm\nImmigration · Family · Real Estate',
      },
      visible: true,
    },
  ],
  contact: {
    firmName: {
      zh: '',
      en: 'Y. Liu Law Firm',
    },
    pageTitle: {
      zh: '',
      en: 'Contact Us',
    },
    intro: {
      zh: '',
      en: "We're here to help. Reach out to our offices in Toronto or Beijing.",
    },
    torontoOffice: {
      title: {
        zh: '',
        en: 'Toronto Office',
      },
      addressLines: ['105 Main Street, 2nd Floor', 'Unionville, ON L3R 2G1'],
      phone: '+1 905 604 5521',
      fax: '+1 905 604 5531',
      email: 'lawyer@yliu.ca',
    },
    beijingOffice: {
      title: {
        zh: '',
        en: 'Beijing Office',
      },
      addressLines: ['3rd Floor China World Office 1', 'No. 1 Jian Guo Man Wai Ave', 'Beijing 10004'],
      phone: '+86 10 65057866',
    },
    footerTagline: {
      zh: '',
      en: 'Professional Legal Excellence across borders. Toronto | Beijing',
    },
  },
});

export const normalizeSiteContent = (data: Partial<SiteContentDoc> | undefined): SiteContentDoc => {
  const defaults = createDefaultSiteContent();
  const incomingServices = data?.services || [];
  const incomingServicesById = new Map(incomingServices.map((service) => [service.id, service]));

  return {
    landing: {
      ...defaults.landing,
      ...data?.landing,
      heroSubtitle: {
        ...defaults.landing.heroSubtitle,
        ...data?.landing?.heroSubtitle,
      },
      aboutTitle: {
        ...defaults.landing.aboutTitle,
        ...data?.landing?.aboutTitle,
      },
      aboutIntro: {
        ...defaults.landing.aboutIntro,
        ...data?.landing?.aboutIntro,
      },
      crossBorderTitle: {
        ...defaults.landing.crossBorderTitle,
        ...data?.landing?.crossBorderTitle,
      },
      crossBorderParagraphs: (data?.landing?.crossBorderParagraphs || defaults.landing.crossBorderParagraphs).map((paragraph, index) => ({
        ...defaults.landing.crossBorderParagraphs[index],
        ...(paragraph || {}),
      })),
      aboutImage: {
        ...defaults.landing.aboutImage,
        ...data?.landing?.aboutImage,
        alt: {
          ...defaults.landing.aboutImage.alt,
          ...data?.landing?.aboutImage?.alt,
        },
        caption: {
          ...defaults.landing.aboutImage.caption,
          ...data?.landing?.aboutImage?.caption,
        },
      },
      teamTitle: {
        ...defaults.landing.teamTitle,
        ...data?.landing?.teamTitle,
      },
      teamIntro: {
        ...defaults.landing.teamIntro,
        ...data?.landing?.teamIntro,
      },
    },
    services: defaults.services.map((fallbackService) => {
      const incoming = incomingServicesById.get(fallbackService.id);
      return {
        ...fallbackService,
        ...(incoming || {}),
      };
    }),
    team: (data?.team || defaults.team).map((member, index) => {
      const fallback = defaults.team[index] || defaults.team[0];
      return {
        ...fallback,
        ...member,
        name: {
          ...fallback.name,
          ...member.name,
        },
        imageAlt: {
          ...fallback.imageAlt,
          ...member.imageAlt,
        },
        imageCaption: {
          ...fallback.imageCaption,
          ...member.imageCaption,
        },
        summary: {
          ...fallback.summary,
          ...member.summary,
        },
      };
    }),
    contact: {
      ...defaults.contact,
      ...data?.contact,
      firmName: {
        ...defaults.contact.firmName,
        ...data?.contact?.firmName,
      },
      pageTitle: {
        ...defaults.contact.pageTitle,
        ...data?.contact?.pageTitle,
      },
      intro: {
        ...defaults.contact.intro,
        ...data?.contact?.intro,
      },
      torontoOffice: {
        ...defaults.contact.torontoOffice,
        ...data?.contact?.torontoOffice,
        title: {
          ...defaults.contact.torontoOffice.title,
          ...data?.contact?.torontoOffice?.title,
        },
      },
      beijingOffice: {
        ...defaults.contact.beijingOffice,
        ...data?.contact?.beijingOffice,
        title: {
          ...defaults.contact.beijingOffice.title,
          ...data?.contact?.beijingOffice?.title,
        },
      },
      footerTagline: {
        ...defaults.contact.footerTagline,
        ...data?.contact?.footerTagline,
      },
    },
  };
};
